/* eslint-disable no-unused-vars */
import { AxiosError } from 'axios';
import { Location } from 'components/shared/LastLocationProvider';
import TooltipDto from 'components/shared/tooltip/types/TooltipDto';
import { FormApi } from 'final-form';
import { Moment } from 'moment';
import { SortBy } from 'shared-client/types/index';
import { ValidationErrorSource } from 'util/Misc';
import EntityDefinition from './EntityDefinition';
import BaseDto from './__models__/BaseDto';
import BasicDto from './__models__/BasicDto';
import { ModelStatus } from 'shared-client/types/model-status';
import RecordScope from './__models__/RecordScope';
import { MapRule } from '../../../Roc.SharedClientApp/types/commerce/product-details';
import { TokenAvailability } from 'components/shared/AvailableReplacementTokens';

export { default as EntityDefinition } from './EntityDefinition';

/**
 * Expected fields in responses from the server in listing pages
 */
export interface IndexResults<TModel extends BasicDto> {
	page: number;
	pageSize: number;
	totalPages: number;
	totalRecords: number;
	records: TModel[];
	parentRecords: ParentRecordDto[] | null;
	basicSearchFields: string[];
	sortBys: SortBy[];
	appliedSortBy: SortBy;
	uniqueId: string;
	appliedSavedFilterId: string;
	hasQueuedBulkDelete?: boolean;
}

export interface ParentRecordDto {
	id: string;
	name: string;
	parent: ParentRecordDto;
}

/**
 * The admin edit query result object. This should match the shape of `EditQueryResultBase<T>`
 */
export interface QueryResponse<TDto> {
	/**
	 * The entity that will populate the admin form
	 */
	entity: TDto;

	/**
	 * Archived versions of the entity (models that implement <see cref="BaseModels.IVersionableModel"/> only)
	 */
	previousVersions?: EntityVersion[];

	/**
	 * Pending drafts for this entity (models that implement <see cref="BaseModels.IVersionableModel"/> only)
	 */
	pendingVersions?: EntityVersion[];

	/**
	 * Scheduled changes for this entity (models that implement <see cref="BaseModels.IVersionableModel"/> only)
	 */
	scheduledVersions?: EntityVersion[];

	/**
	 * The status for the current version, which is the one that you go to when clicking on a listing on a listing
	 * page (models that implement <see cref="BaseModels.IVersionableModel"/> only)
	 */
	currentVersionStatus: ModelStatus;
}

/**
 * Represents the state of an admin edit form.
 * @template TDto The dto object representing the entity being edited or created
 * @template TQueryResponse The dto response object sent from the backend
 */
export interface EditContextState<TDto = any, TQueryResponse extends QueryResponse<TDto> = QueryResponse<TDto>> {
	values: TDto | undefined;
	response: TQueryResponse | undefined;
	editSessionUniqueKey: string;
	entityDefinition: EntityDefinition;

	primaryCulture?: string;
	currentCulture?: string;
	redirectCulture?: string;
	currentScope?: RecordScope;

	/**
	 * The error when trying to save or send data to the server.
	 */
	axiosError: AxiosError | null;

	/**
	 * If true, there was an error trying to save data on the server that couldn't be handled properly.
	 * (typically a timeout or internal server error)
	 */
	errorOnSave: boolean;

	/**
	 * If true, there was an error trying to save data on the server because the data failed
	 * validation (eg the user sent bad data to the server)
	 */
	validationError: boolean;

	/**
	 * The source of any validation errors.
	 */
	validationErrorSource: ValidationErrorSource;

	/**
	 * Is the edit context doing an initial page load?
	 */
	isLoadingInitial: boolean;

	/**
	 * Is the edit context doing a post-initial page load, such as when a different version is selected?
	 */
	isLoading: boolean;

	/**
	 * True whenever the form is being saved.
	 */
	isSaving: boolean;

	/**
	 * The pathname to redirect to. For example: /cms/pages
	 */
	redirectPathName: string;

	/**
	 * The query string to append to the path name when redirecting. For example: q=test
	 */
	redirectSearch: string;

	/**
	 * True when an item is being created.
	 */
	isCreateMode: boolean;

	/**
	 * The ID of the item being edited, based on the route
	 */
	editingId?: string;

	/**
	 * Triggers a save and continue maintaining the historyId in the QueryString.
	 */
	continueWithHistory?: boolean;

	/**
	 * Whether the next edit/publish request should be forced, that is, if it should succeed even if it must
	 * modify other models in the database to do so. This should only ever be set to true if the user has been
	 * warned of the consequences ahead of time.
	 */
	force: boolean;

	/**
	 * The previous location when the edit context was initialized. This can be used to navigate back to listing
	 * pages (or whatever previous page was active).
	 */
	lastLocation: Location;

	/**
	 * The type of submission that the next edit form submission should be. This gets set from other components
	 * that are trying to perform actions such as "save as draft", etc. If `null`, the next submission will not
	 * change the entity's status.
	 */
	nextSubmissionType: ModelStatus | null;
	/**
	 * Whether or not the next edit form submission should continue by refreshing the edit form instead of
	 * redirecting back to the previous page (often the listing page).
	 */
	nextSubmissionContinues: boolean;

	nextSubmissionTime: Moment | null;
	hasTooltipError: boolean;
	tooltipData: { [name: string]: { fieldTooltip: TooltipDto; universalTooltip: TooltipDto } };
	entityTypeId: string;

	fieldSuggestions: FieldSuggestionMap;
	scopeFieldEnabled: boolean;
	redirectTo404: boolean;

	/**
	 * True if the scope was changed on the edit page
	 */
	scopeChanged: boolean;

	/**
	 * Contains the record original scope on the edit page
	 */
	originalScope?: RecordScope;
}

/**
 * Represents 301 redirects that should be created when deleting a published record or changing the scope of a published record.
 */
export interface RedirectData {
	/** The new URL to create a 301 redirect for. */
	redirectUrl: string;
	/** The guid of the site that the redirect will belong to. */
	siteId: string;
}

/**
 * The state of the create/edit submission.
 */
export interface SubmissionState {
	/** The type of submission such as publishing or deactivating */
	submissionType: ModelStatus | null;
	/** If set, the time the create/edit action is to be performed */
	submissionTime: Moment | null;
	/** If true, do not redirect the user after the submission attempt. */
	doContinue: boolean;
	/** If true, the submission will override errors if possible. This should only ever be set if the user was warned of the consequences of forcing the submission. */
	force?: boolean;
}

/**
 * The type for a single form values transform handler function.
 */
export type FormValuesTransformHandler = (formValues: any) => Promise<any>;

/**
 * Type for the entire edit context, which includes state as well as additional functions
 * which do not need to be state.
 */
export type EditContext<
	TDto = any,
	TQueryResponse extends QueryResponse<TDto> = QueryResponse<TDto>,
> = EditContextState<TDto, TQueryResponse> & {
	/**
	 * Resets a component's EditContext to its initial state.
	 */
	reset(): void;

	/**
	 * Informs the edit context of the kind of save that should be done when the edit form is submitted. This is used
	 * for flagging certain saves such as saving as draft, inactive, etc.
	 */
	setNextSubmissionState: (nextState: SubmissionState) => Promise<void>;

	/**
	 * Returns a promise that will save the form values on the server. The promise is required for
	 * react-final-form to properly handle form state if something goes wrong. For example, if there's
	 * validation, or a server error.
	 */
	save(values: TDto, formApi?: FormApi): Promise<void> | Record<string, any>;

	/**
	 * Loads the entity being edited based on the current URL. This is called on load but can also
	 * be called from other components via editContext. That method is primarily used by the versioning
	 * dropdown but can be used by anything.
	 */
	load(): void;

	registerOnBeforeSaveCallback(name: string, cb: () => void);

	/**
	 * Retrieves a full list of the version history within the edit context.
	 */
	getAllVersions(): EntityVersion[];
	/**
	 * Returns the unique identifier (from the query string) of the current active version.
	 * This may not necessarily be a version that exists.
	 */
	getActiveVersionId(): string | null;
	/**
	 * Gets the current active version within the edit context, or `null` if the current
	 * version is the latest version.
	 */
	getActiveVersion(): EntityVersion | null;
	/**
	 * Retrieves the `EntityVersion` from the history of the edit context by id, or `null` if
	 * the version could not be found.
	 */
	getVersionById(id?: string): EntityVersion | null;
	/**
	 * Loads the cached tooltip data into the editContext
	 */
	getTooltipsForEntity: () => void;

	/**
	 * Sets the current culture on the page
	 * @param culture
	 */
	setCulture(culture: string): void;

	/**
	 * Sets the continueWithHistory value. When true, the current historyId in the querystring will be maintained after
	 * the Save & Continue operation.
	 * @param continueWithHistory
	 */
	setContinueWithHistory(continueWithHistory: boolean): void;

	/**
	 * Updates the pending field suggestions by merging the provided object with the existing state.
	 * This means that field suggestions that do not match keys in the `fieldSuggestMap` will not be
	 * removed. They may be overwritten if there's a match, however. Similar to how setState works.
	 */
	setFieldSuggestions: (fieldSuggestMap: FieldSuggestionMap) => void;

	/**
	 * Redirects the browser back to the last location before the active edit form was mounted. This
	 * is often used to redirect back to the listing page (and retain any active filters).
	 */
	redirectToLastLocation: () => void;

	/**
	 * Toggles whether the scope field should be enabled or disabled for a scoped entity
	 */
	setScopeFieldEnabled: (display: boolean) => void;

	/**
	 * Set the culture that should be set when the form reloads.
	 */
	setRedirectCulture: (culture?: string) => void;

	/**
	 * Set current scope
	 */
	setCurrentScope: (scope: RecordScope) => void;

	/**
	 * Set scope changed
	 */
	setScopeChanged: (scopeChanged: boolean) => void;

	/**
	 * Set the original scope
	 */
	setOriginalScope: (scope?: RecordScope) => void;

	/**
	 * Adds a new form values transform handler for this edit instance.
	 */
	registerFormValuesTransformHandler: (key: string, handler: FormValuesTransformHandler) => void;
};

/**
 * Type for all maps for field suggestions.
 */
export interface FieldSuggestionMap {
	[index: string]: string;
}

/**
 * Represents the data of a single entity version record.
 */
export class EntityVersion {
	/** The unique identifier of the version record. */
	public id: string | null;
	public createDate: Moment;
	public modifyDate: Moment;
	public processTime?: Moment | null;
	/** If specified, the time that this entity version is scheduled to be applied. */
	public scheduledTime?: Moment | null;
	public status?: string;
	/** The display name of the user who created this entity version. */
	public createUser?: string;
	/** The display name of the user who modified this entity version. */
	public modifyUser?: string;
	public versionNo: number;
	public type: EntityVersionType;
}

export enum EntityVersionType {
	/**
	 * The current version of the entity.
	 */
	Current,
	/**
	 * A future version of the entity (such as a scheduled edit or delete).
	 */
	Future,
	/**
	 * A pending draft version of the entity.
	 */
	Draft,
	/**
	 * A previous version of the entity.
	 */
	Archived,

	// NOTE: if more version types are added, `HistoryHelper.getVersionTypeName` should be updated!
}

/**
 * Type for storing state withing listing pages
 */
// TODO: apply TModel to `results`
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface TreeContextState<TModel extends BasicDto> {
	results?: any;
	entityDefinition: EntityDefinition;
	isLoading: boolean;
	hasError: boolean;
	height: number;
}

/**
 * Type for the entire tree context, which includes state as well as additional functions
 * which do not need to be state.
 */
export type TreeContext<TModel extends BasicDto> = TreeContextState<TModel> & {
	handleDelete(record: BaseDto, nameField: string): void;
	updateTree(treeData: any): void;
	onVisibilityToggle(options: any): void;
};

/**
 * Represents the various global application settings used in the admin.
 */
export interface AppSettings {
	isTestMode: boolean;

	/** The IANA time zone string which designates how all dates and times in the admin will display. */
	timeZone: string;
	maximumIdleTimeBeforeSessionExpirationCountdown: number;
	sessionExpirationFinalCountdown: number;
	sessionTimeout: number;
	isMetaKeywordVisible: boolean;
	option1Text404: string;
	option2Text404: string;
	option1Link404: string;
	option2Link404: string;
	option1Text500: string;
	option2Text500: string;
	option1Link500: string;
	option2Link500: string;
	userRequireConfirmEmail: boolean;
	duoSecurityForLdapOnly: boolean;
	text404: string;
	text500: string;
	enableCKEditorInspector: boolean;
	enableCKEditorPreviewEmbeddedHtml: boolean;
	ckEditorLicenseKey: string;
	enableCKEditorSanitizeEmbeddedHtml: boolean;
	isAccessControlEnabled: boolean;
	isPaymentAccessControlEnabled: boolean;
	// #region Google
	ga4DashboardUrl: string;
	googleDataStudioUrl: string;
	// #endregion
	// #region Commerce
	defaultCurrency: string;
	enableMultipleUserOrganizations: boolean;
	enableMinimumOrderQuantities: boolean;
	enableDecimalQuantities: boolean;
	defaultSellPack: number;
	mapDefaultSetting: MapRule;
	// #endregion
	pageTitlePrefix: string | null;
	pageTitleSuffix: string | null;
	homePageDefaultTitleTag: string | null;
	homePageDefaultH1: string | null;
	homePageDefaultMetaDescription: string | null;
	shopByCategoryDefaultTitleTag: string | null;
	shopByCategoryDefaultH1: string | null;
	shopByCategoryDefaultMetaDescription: string | null;
	shopByBrandDefaultTitleTag: string | null;
	shopByBrandDefaultH1: string | null;
	shopByBrandDefaultMetaDescription: string | null;
	pdpDefaultTitleTag: string | null;
	pdpDefaultH1: string | null;
	pdpDefaultMetaDescription: string | null;
	storeLocatorDefaultTitleTag: string | null;
	storeLocatorDefaultH1: string | null;
	storeLocatorDefaultMetaDescription: string | null;
	locationPageDefaultTitleTag: string | null;
	locationPageDefaultH1: string | null;
	locationPageDefaultMetaDescription: string | null;
	defaultAssetStorageProviderDisplayName: string | null;
}

/**
 * interface for the initial app config
 */
export interface AppConfig {
	adminUrl: string;
	apiUrl: string;
	tasksUrl: string;
	multiSiteEnabled: boolean;
	duoSecurityEnabled: boolean;
	settingScopePrefix: string;
	useEmailAsUserName: boolean;
	isTestingEnvironment: boolean;
	version: string;
	build: string;
	hasPendingMigrations: boolean;
	// #region Commerce
	categoryTreesEnabled: boolean;
	// #endregion
	datadog: {
		applicationId: string;
		clientToken: string;
		site: string;
		service: string;
		env: string;
	};
}

/**
 * interface for the initial logging config
 */
export interface LoggingConfig {
	centralLoggingUrl: string;
	isSqlLoggingEnabled: boolean;
}

/**
 * Represents the application user's authorized routes (access to various features) and various
 * other permissions not specifically associated with base routes.
 * */
export interface AppUserPermissions {
	/** Whether the current user is a power user (has all permissions) */
	isPowerUser: boolean;

	/** A dictionary with routes as keys and a list of permissions for values. This list includes the basic permissions: [view, edit/create, delete] */
	authorizedBaseRoutes: { [baseRoute: string]: boolean[] };

	/** Whether the user has permission to requeue emails */
	hasReQueueEmailPermission: boolean;

	/** Whether the user has permission to manage jobs */
	hasListTasksPermission: boolean;

	/** Whether the user can manage settings */
	hasManageSettingsPermission: boolean;

	/** Whether hasImpersonationPermission user can initiate impersonation sessions */
	hasImpersonationPermission: boolean;

	/** Whether the user can view the redis status page */
	hasViewRedisStatusPermission: boolean;

	/** Has list permissions to any admin area. Used in the Export page, to display the create button if the user has access to view any area, as this means data could be potentially exported. */
	hasAnyListPermission: boolean;

	/** Has edit permission to any admin area. Used in the Import page, to display the create button if the user has access to view any area, as this means data could be potentially imported. */
	hasAnyEditPermission: boolean;

	/** If true, the user has access to reset Two-Factor Authentication of other users */
	hasResetTwoFactorAuthenticatonPermission: boolean;
}

/**
 * Represents an application user (a logged in administrator)
 */
export interface AppUser {
	/** The unique identifier of the application user */
	id: string;

	/** The first name of the application user */
	firstName: string;

	/** The last name of the application user */
	lastName: string;

	/** The user name of the application user (their login). */
	userName: string;

	/** The email of the application user */
	email: string;

	/** If true, this user is an application developer and will see developer only / debug features restricted from normal administrators. */
	isDeveloper?: boolean;

	/** If true, the tooltip edit mode is on */
	isTooltipEditMode: boolean;

	/** If true, show the tooltip edit mode toggle */
	showIsTooltipEditMode: boolean;

	/** Contains the authorized base routes for the logged in user
	 * for eg.: [{'/ajax/pages': [true, true, false]}, {...}]
	 * for index, createEdit and delete permissions for the pages feature
	 */
	authorizedBaseRoutesData: AppUserPermissions;

	/** If true, the user has access to upload assets */
	hasUploadAssetPermission: boolean;

	/**If true the user has 2FA enabled */
	hasTwoFactorEnabled: boolean;

	// #region Commerce
	/** If true, the user has the permission to create a credit memo */
	hasCreateCreditMemoPermission: boolean;
	// #endregion
}

/**
 * Represents an Asset Storage Provider in the App Context
 *
 * @export
 * @interface AssetStore
 */
export interface AssetStore {
	id: string;
	name: string;
}

/**
 * Represents an application message
 */
export interface AppMessage {
	message: string;
	link: string;
}

/**
 * The initial front end application state sent by the server before it is transformed into other classes and structues. AppPreloadedState has a very brief life cycle.
 */
export interface AppPreloadedState {
	settings: AppSettings;
	user: AppUser;
	assetStores: AssetStore[];
	messages: AppMessage[];
}

export interface AppCulture {
	id: string;
	name: string;
	code: string;
	isFallback: boolean;
}

export interface AppSiteDto {
	id: string;
	name: string;
	isDefault: boolean;
	cultureId: string;
	siteCollections: AppSiteCollectionSiteDto[];
	hosts: AppSiteHostDto[];
	canonicalCultureHosts: AppCanonicalCultureHostDto[];
}

// #region Commerce
export interface AppSiteDto {
	isOrderNotesEnabled: boolean;
	isOrderCreditMemosEnabled: boolean;
}
// #endregion

export interface AppSiteCollectionDto {
	id: string;
	name: string;
	sites: AppSiteCollectionSiteDto[];
}

export interface AppSiteCollectionSiteDto {
	siteId: string;
	siteCollectionId: string;
}

export interface AppSiteHostDto {
	id: string;
	host: string;
	port?: number;
	cultureId: string | null;
	subdirectory: string | null;
}

export interface AppCanonicalCultureHostDto {
	siteId: string;
	siteHostId: string;
	cultureId: string;
}

// #region Commerce
export interface SiteCategoryTreeDto {
	id: string;
	name: string;
	siteIds: string[];
}
// #endregion

/**
 * List of fields that allow for replacement tokens
 */
export interface ReplacementTokenFields {
	availabilities: TokenAvailability[];
	fields: string[];
}
